import { useState, useCallback } from 'react';
import type { SyntheticEvent } from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';

import { EMPTY_STRING } from '@e-socna/empty';

import { PATH_MY_ACCOUNT_DEFAULT } from '../../../app/constants/paths.constant';
import { useAuthenticationContext } from '../../../contexts/AuthenticationContext/hooks/useAuthenticationContext.hook';
import { MenuItem } from '../../../components/Menu/MenuItem';
import { useLogout } from '../../../hooks/useLogout.hook';
import { Avatar } from '../../../components/Avatar';
import type { Option } from '../../../components/Menu/MenuItem/types/option.type';

export const UserMenu = () => {
  const [anchorElUser, setAnchorElUser] = useState<Element | null>(null);
  const isUserMenuOpen = Boolean(anchorElUser);

  const { authenticationData } = useAuthenticationContext();
  const logout = useLogout();

  const options: readonly Option[] = [
    {
      key: 'account',
      title: 'Mon compte',
      to: PATH_MY_ACCOUNT_DEFAULT,
    },
    {
      key: 'logout',
      title: 'Déconnexion',
      action: logout,
    },
  ] as const;

  const person = authenticationData?.person;

  const fullName = person
    ? `${person.firstName ? `${person.firstName} ` : EMPTY_STRING}${person.lastName}`
    : EMPTY_STRING;

  const handleOpenUserMenu = useCallback(
    (event: SyntheticEvent<HTMLButtonElement>) => {
      setAnchorElUser(event.currentTarget);
    },
    [],
  );

  const handleCloseUserMenu = useCallback(() => {
    setAnchorElUser(null);
  }, []);

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Ouvrir le menu de l'utilisateur">
        <IconButton
          aria-controls={
            isUserMenuOpen ? 'user-actions-appbar-menu' : undefined
          }
          aria-expanded={isUserMenuOpen ? 'true' : undefined}
          aria-haspopup="true"
          aria-label="Ouvrir le menu de l'utilisateur"
          id="user-actions-appbar-menu-button"
          onClick={handleOpenUserMenu}
          sx={{ p: 0 }}
        >
          <Avatar
            bgColor="#fff"
            fullName={fullName}
            imageUri={person?.pictureImageUri}
            sx={{ color: 'secondary.main' }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id="user-actions-appbar-menu"
        keepMounted
        onClose={handleCloseUserMenu}
        open={isUserMenuOpen}
        slotProps={{
          list: {
            'aria-labelledby': 'user-actions-appbar-menu-button',
          },
        }}
        sx={{ mt: '45px' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {options.map((option: Option) => (
          <MenuItem
            key={option.key}
            onCloseMenu={handleCloseUserMenu}
            option={option}
          />
        ))}
      </Menu>
    </Box>
  );
};
